<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import InfoTooltip from "@/components/InfoTooltip";
import AutoRefreshToggle from "@/components/AutoRefreshToggle";
import HashrateChart from "@/components/HashrateChart";
import NavigationWrapper from "@/components/NavigationWrapper";
import { formatHashrate, getBreakpoint } from "@/utils";

export default {
  // Included components
  components: {
    FontAwesomeIcon,
    NavigationWrapper,
    HashrateChart,
    InfoTooltip,
    AutoRefreshToggle,
  },

  data() {
    return {
      listView: false,
    };
  },

  computed: {
    // List of group IDs to show
    groupIds() {
      return Object.values(this.groups).map((group) => {
        return group.id;
      });
    },

    // Mapped vuex state
    ...mapGetters(["can"]),
    ...mapState(["groups", "groupStatistics", "groupShares"]),
  },

  // On creation fetch the team's groups and their statistics and hashrate
  async created() {
    await this.getGroups();
    this.updateHashrateChartData();
    this.updateStatistics();
  },

  methods: {
    // Update the hashrate chart data (every 5 minutes if autorefresh is on)
    async updateHashrateChartData() {
      await this.getGroupSharesMulti({ groupIds: this.groupIds });
    },

    // Update the group statistics (every 30 seconds if autorefresh is on)
    async updateStatistics() {
      await this.getGroupStatisticsMulti({ groupIds: this.groupIds });
    },

    groupAlerts(id) {
      return Object.values(this.alerts).filter((alert) => {
        return alert.group_id == id;
      });
    },

    triggeringAlertsCount(id) {
      return this.groupAlerts(id).filter(
        (alert) => alert.triggering && alert.enabled
      ).length;
    },

    // Helper methods and mapped vuex actions
    formatHashrate,
    getBreakpoint,
    ...mapActions([
      "getGroups",
      "getGroupStatisticsMulti",
      "getGroupSharesMulti",
    ]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item active>Groups</b-breadcrumb-item>
    </template>
    <b-row class="mb-3">
      <b-col cols="7">
        <h1>Groups</h1>
        <AutoRefreshToggle
          v-if="
            !(Object.keys(groups).length === 0) &&
            (getBreakpoint().xs || getBreakpoint().sm)
          "
          @update-5m="updateHashrateChartData()"
          @update-30s="updateStatistics()"
        />
      </b-col>
      <b-col
        cols="5"
        :align-self="getBreakpoint().xs ? '' : 'center'"
        v-if="!(Object.keys(groups).length === 0)"
      >
        <div class="float-right">
          <AutoRefreshToggle
            v-if="!(getBreakpoint().xs || getBreakpoint().sm)"
            @update-5m="updateHashrateChartData()"
            @update-30s="updateStatistics()"
          />
          <b-button
            v-if="can.groups.edit"
            :class="getBreakpoint().xs ? 'mt-2' : 'ml-3'"
            id="listview"
            variant="light"
            :size="getBreakpoint().xs ? 'sm' : 'md'"
            :pressed.sync="listView"
          >
            <FontAwesomeIcon v-if="!listView" icon="list" size="lg" />
            <FontAwesomeIcon v-else icon="th" size="lg" />
          </b-button>
          <b-button
            v-if="can.groups.edit"
            :class="getBreakpoint().xs ? 'mt-2 ml-1' : 'ml-2'"
            variant="primary"
            to="/groups/create"
            :size="getBreakpoint().xs ? 'sm' : 'md'"
          >
            {{ getBreakpoint().xs ? "New" : "Add Group" }}
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="Object.keys(groups).length === 0">
      <b-col>
        <b-card class="text-center py-5">
          <b-card-title>No Groups</b-card-title>
          <b-card-text>
            Groups are used to organize workers and display aggregate statistics
          </b-card-text>
          <b-button
            v-if="can.groups.edit"
            variant="primary"
            to="/groups/create"
          >
            Add Group
          </b-button>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-else-if="!listView">
      <b-col
        lg="6"
        v-for="(group, index) in Object.values(groups)"
        :key="index"
      >
        <b-card class="Group-card Card-hoverable">
          <b-link :to="`/groups/${group.id}`">
            <b-card-title>
              <div class="Group-card-title">
                {{ group.name }}
                <span class="Group-card-link-indicator">
                  <font-awesome-icon icon="chevron-right" size="xs" />
                </span>
              </div>
            </b-card-title>
            <b-card-sub-title class="mb-2">
              {{
                formatHashrate(
                  groupStatistics[group.id]
                    ? groupStatistics[group.id].hashrate
                    : 0
                )
              }}
              <InfoTooltip text="30 minute average hashrate" />
            </b-card-sub-title>
            <b-row>
              <b-col cols="4">
                <b-card-text>
                  <b>Active Workers</b>
                  <p>
                    {{
                      groupStatistics[group.id]
                        ? groupStatistics[group.id].active_count
                        : "—"
                    }}
                  </p>
                </b-card-text>
              </b-col>
              <b-col cols="4">
                <b-card-text>
                  <b>Total Workers</b>
                  <p>
                    {{
                      groupStatistics[group.id]
                        ? groupStatistics[group.id].count
                        : "—"
                    }}
                  </p>
                </b-card-text>
              </b-col>
              <b-col cols="4">
                <b-card-text>
                  <b>Alert Status</b>
                  <p
                    v-if="
                      groupStatistics[group.id] &&
                      groupStatistics[group.id].triggering_alerts > 0
                    "
                  >
                    <b-badge variant="danger">ALERT</b-badge>
                    &times;
                    {{
                      groupStatistics[group.id]
                        ? groupStatistics[group.id].triggering_alerts
                        : 0
                    }}
                  </p>
                  <p v-else>
                    <b-badge variant="success">OK</b-badge>
                  </p>
                </b-card-text>
              </b-col>
            </b-row>

            <HashrateChart
              :data="
                groupShares[group.id]
                  ? groupShares[group.id].map((share) => [
                      share[0],
                      share[1] / 300,
                    ])
                  : []
              "
              :canExport="false"
            />
          </b-link>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-else>
      <b-col>
        <b-table-simple striped hover>
          <b-thead>
            <b-tr>
              <b-th>Name</b-th>
              <b-th>Hashrate</b-th>
              <b-th>Active Workers</b-th>
              <b-th>Total Workers</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(group, index) in Object.values(groups)" :key="index">
              <b-td>
                <b-link :to="`/groups/${group.id}`">{{ group.name }}</b-link>
              </b-td>
              <b-td>
                {{
                  formatHashrate(
                    groupStatistics[group.id]
                      ? groupStatistics[group.id].hashrate
                      : 0
                  )
                }}
              </b-td>
              <b-td>
                {{
                  groupStatistics[group.id]
                    ? groupStatistics[group.id].active_count
                    : "—"
                }}
              </b-td>
              <b-td>{{ group.worker_ids.length }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>

<style scoped>
.Group-card {
  margin-bottom: 2rem;
}

.Group-card-title {
  display: flex;
  align-items: center;
}

.Group-card .Group-card-link-indicator {
  display: inline-flex;
  align-items: center;

  opacity: 0;

  transition: all 200ms;
}

.Group-card:hover .Group-card-link-indicator {
  opacity: 1;

  margin-left: 0.35rem;
}

.Group-card a {
  color: unset;
  font-weight: unset;
  text-decoration: unset;
}

.Group-card:hover .Group-card-title {
  color: #007bff;
}
</style>
